import { useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Clock, FileText, Search, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  approveProfileDocumentApi,
  getProfileDocumentRequestsApi,
  rejectProfileDocumentApi,
} from "../../app/api";

const IMAGE_BASE = "https://uptask.visiontrek.io";

export default function ApproveProfileDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadDocuments = async () => {
    setLoading(true);
    const res = await getProfileDocumentRequestsApi();
    setDocuments(res.success ? res.data || [] : []);
    setLoading(false);
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const counts = useMemo(
    () => ({
      PENDING: documents.filter((doc) => doc.status === "PENDING").length,
      APPROVED: documents.filter((doc) => doc.status === "APPROVED").length,
    }),
    [documents]
  );

  const filteredDocuments = useMemo(() => {
    const value = search.trim().toLowerCase();
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    return documents.filter((doc) => {
      if (doc.status !== activeTab) return false;

      const docDate = new Date(doc.reviewed_at || doc.created_at).getTime();
      if (fromTime && docDate < fromTime) return false;
      if (toTime && docDate > toTime) return false;

      if (!value) return true;
      const name = `${doc.first_name || ""} ${doc.last_name || ""}`.toLowerCase();
      return (
        name.includes(value) ||
        String(doc.emp_id || "").toLowerCase().includes(value) ||
        String(doc.email || "").toLowerCase().includes(value) ||
        String(doc.document_label || doc.document_type || "").toLowerCase().includes(value)
      );
    });
  }, [documents, activeTab, search, dateFrom, dateTo]);

  const approve = async (id) => {
    const res = await approveProfileDocumentApi(id);
    if (res.success) {
      toast.success("Document approved");
      loadDocuments();
    } else {
      toast.error(res.message || "Unable to approve document");
    }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) {
      toast.error("Please add a reject reason");
      return;
    }

    const res = await rejectProfileDocumentApi(id, rejectReason.trim());
    if (res.success) {
      toast.success("Document rejected");
      setRejectingId(null);
      setRejectReason("");
      loadDocuments();
    } else {
      toast.error(res.message || "Unable to reject document");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pb-10 pt-28 md:pt-32">
      <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#13072f] text-white shadow-2xl">
        <div className="border-b border-white/10 bg-gradient-to-br from-[#2f1a68] via-[#25104f] to-[#120628] p-6">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-cyan-400/15 text-cyan-200 ring-1 ring-cyan-200/30">
                <FileText size={26} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.32em] text-cyan-200/80">
                  Admin review
                </p>
                <h1 className="text-2xl font-semibold">Approve Profile Documents</h1>
                <p className="text-sm text-white/60">
                  Review Aadhaar, PAN, license and resume uploads before they are accepted.
                </p>
              </div>
            </div>

            <label className="flex h-12 min-w-[280px] items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4">
              <Search size={18} className="text-white/50" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email, ID or document..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-white/40"
              />
            </label>
          </div>

          <div className="mt-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="inline-flex w-fit rounded-2xl border border-white/10 bg-black/20 p-1">
              <TabButton active={activeTab === "PENDING"} label="Pending Documents" count={counts.PENDING} onClick={() => setActiveTab("PENDING")} />
              <TabButton active={activeTab === "APPROVED"} label="Approved Documents" count={counts.APPROVED} onClick={() => setActiveTab("APPROVED")} />
            </div>

            <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100/70">
                <CalendarDays size={16} />
                Date Range
              </div>
              <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
              <DateInput label="To" value={dateTo} onChange={setDateTo} />
              <button
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="h-10 rounded-xl border border-white/15 px-4 text-sm text-white/70 hover:bg-white/10"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5 md:p-6">
          {loading && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/60">
              Loading documents...
            </div>
          )}

          {!loading && filteredDocuments.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-12 text-center">
              <Clock className="mx-auto mb-3 text-white/40" size={34} />
              <h2 className="text-lg font-semibold">
                No {activeTab === "PENDING" ? "pending" : "approved"} documents found
              </h2>
              <p className="text-sm text-white/50">
                Employee document uploads will appear here.
              </p>
            </div>
          )}

          {!loading &&
            filteredDocuments.map((doc) => (
              <article key={doc.id} className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-xl">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-cyan-400/10 text-cyan-200">
                        <FileText size={22} />
                      </span>
                      <div>
                        <h2 className="font-semibold">{doc.document_label}</h2>
                        <p className="text-xs text-white/50">
                          {doc.first_name} {doc.last_name} • ID {doc.emp_id || "-"} • {doc.email || "No email"}
                        </p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-white/60">{doc.original_name || doc.file_path}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={`${IMAGE_BASE}${doc.file_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border border-cyan-200/25 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-100"
                    >
                      View Document
                    </a>
                    <StatusBadge status={doc.status} />
                  </div>
                </div>

                {doc.status === "REJECTED" && doc.admin_reason && (
                  <div className="mt-4 rounded-2xl border border-red-300/15 bg-red-400/10 p-3 text-sm text-red-100">
                    Reject reason: {doc.admin_reason}
                  </div>
                )}

                {doc.status === "PENDING" && (
                  <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                    {rejectingId === doc.id && (
                      <input
                        value={rejectReason}
                        onChange={(event) => setRejectReason(event.target.value)}
                        placeholder="Reason for rejection..."
                        className="h-11 min-w-[260px] rounded-xl border border-white/15 bg-white/10 px-3 text-sm outline-none placeholder:text-white/40"
                      />
                    )}

                    {rejectingId === doc.id ? (
                      <button onClick={() => reject(doc.id)} className="rounded-xl bg-red-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20">
                        Confirm Reject
                      </button>
                    ) : (
                      <button onClick={() => setRejectingId(doc.id)} className="rounded-xl border border-red-300/30 bg-red-400/10 px-5 py-3 text-sm font-semibold text-red-100">
                        Reject
                      </button>
                    )}

                    <button onClick={() => approve(doc.id)} className="rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20">
                      Approve Document
                    </button>
                  </div>
                )}
              </article>
            ))}
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
        status === "PENDING"
          ? "bg-amber-400/15 text-amber-200"
          : status === "APPROVED"
          ? "bg-emerald-400/15 text-emerald-200"
          : "bg-red-400/15 text-red-200"
      }`}
    >
      {status === "PENDING" && <Clock size={14} />}
      {status === "APPROVED" && <CheckCircle2 size={14} />}
      {status === "REJECTED" && <XCircle size={14} />}
      {status}
    </span>
  );
}

function TabButton({ active, label, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition ${
        active
          ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg shadow-cyan-500/20"
          : "text-white/60 hover:bg-white/10 hover:text-white"
      }`}
    >
      <span>{label}</span>
      <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-white/20" : "bg-white/10"}`}>
        {count}
      </span>
    </button>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <label className="flex h-10 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3">
      <span className="text-xs text-white/45">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="bg-transparent text-sm text-white outline-none [color-scheme:dark]"
      />
    </label>
  );
}
